'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { generateCampaignSlug } from '@/lib/nanoid-utils';
import { usePostHog } from 'posthog-js/react';

import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import {
  CampaignLocations,
  CampaignLocationsRef,
} from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { useWizardHost } from '@/components/campaign/new/wizard-host-context';
import { LandingPageStep, useLandingPageStep } from '@/components/campaign/new/landing-page-step';
import {
  Location,
  CAMPAIGN_TYPE,
  CAMPAIGN_STATE,
  APPROVAL_STATE,
  type CampaignData,
  type CampaignProximityInput,
} from '@/lib/types';
import { setCampaignProximityTargets } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import {
  useCreateCompetitorCampaignMutation,
  businessQueryOptions,
  competitorsByBusinessQueryOptions,
  campaignQueryOptions,
} from '@/lib/query-options';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { useRefetchCampaigns } from '@/hooks/useRefetchCampaign';
import { useAtomValue, useAtom } from 'jotai';
import { organizationDataAtom, campaignCreationPreSelectionAtom } from '@/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/competitor')({
  component: CampaignCreation,
});

// Steps for the competitor campaign creation flow
enum CAMPAIGN_CREATION_STEP {
  SELECT_COMPETITORS = 'SELECT_COMPETITORS',
  SELECT_LANDING_PAGE = 'SELECT_LANDING_PAGE',
  SELECT_LOCATIONS = 'SELECT_LOCATIONS',
}

const STEP_CONFIG: Record<
  CAMPAIGN_CREATION_STEP,
  {
    stepIndicator: number;
    title: string;
    backStep: CAMPAIGN_CREATION_STEP | null;
  }
> = {
  [CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS]: {
    stepIndicator: 0,
    title: 'Select Competitors',
    backStep: null, // First step - back exits the wizard
  },
  [CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE]: {
    stepIndicator: 1,
    title: 'Select Landing Page',
    backStep: CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS,
  },
  [CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS]: {
    stepIndicator: 2,
    title: 'Select Locations',
    backStep: CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE,
  },
};

export function CampaignCreation() {
  const navigate = useNavigate();
  const host = useWizardHost();
  const { toast } = useToast();
  const posthog = usePostHog();

  // Get pre-selection from atom (set by competitors page)
  const [preSelection, setPreSelection] = useAtom(campaignCreationPreSelectionAtom);

  // Organization and business data
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Competitors data for competitor campaign selection
  const { data: competitors } = useQuery({
    ...competitorsByBusinessQueryOptions(business?.id || 0),
    enabled: !!business?.id,
  });

  // Filter out competitors already in campaigns
  const availableCompetitors = (competitors?.filter((c) => !c.campaign) || [])
    .sort((a, b) => a.competitor_brand_name.localeCompare(b.competitor_brand_name));

  // State - always start at SELECT_COMPETITORS
  const [currentStep, setCurrentStep] = useState<CAMPAIGN_CREATION_STEP>(
    CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS
  );
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<Set<string>>(
    new Set(preSelection.competitorIds)
  );
  const [locations, setLocations] = useState<Location[]>(
    business?.target_locations || []
  );
  const { selectedLandingPage, setSelectedLandingPage } = useLandingPageStep(business);
  const [stepValid, setStepValid] = useState(false);
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState('');

  // Filter competitors based on search query
  const filteredCompetitors = useMemo(() => {
    if (!competitorSearchQuery.trim()) {
      return availableCompetitors;
    }
    const query = competitorSearchQuery.toLowerCase();
    return availableCompetitors.filter(
      (c) =>
        c.competitor_brand_name.toLowerCase().includes(query) ||
        (c.primary_website && c.primary_website.toLowerCase().includes(query))
    );
  }, [availableCompetitors, competitorSearchQuery]);


  const campaignLocationsRef = useRef<CampaignLocationsRef>(null);
  // #5444: radii chosen on the wizard's Radius tab, saved post-create.
  const [radii, setRadii] = useState<CampaignProximityInput[]>([]);

  // Mutations
  const createCompetitorCampaignMutation = useCreateCompetitorCampaignMutation();
  const { refetchWithDelayedUpdates } = useRefetchCampaigns();
  const queryClient = useQueryClient();

  const isLoading = createCompetitorCampaignMutation.isPending;

  useLoadingOverlay(isLoading);

  // Use business target locations as default when available
  useEffect(() => {
    if (business?.target_locations && locations.length === 0) {
      setLocations(business.target_locations);
    }
  }, [business?.target_locations]);

  // Clear pre-selection on unmount. Modal-hosted, BACK unmounts this wizard
  // to return to the type picker, which restores its selection FROM the atom,
  // so the clear would wipe the highlight; the modal clears the atom when it
  // closes instead.
  useEffect(() => {
    if (host) return;
    return () => {
      setPreSelection({ types: [], competitorIds: [], origin: undefined });
    };
  }, []);

  // Select all available competitors by default if none were pre-selected
  const hasSetDefaultCompetitors = useRef(false);
  useEffect(() => {
    if (!hasSetDefaultCompetitors.current && preSelection.competitorIds.length === 0 && availableCompetitors.length > 0) {
      hasSetDefaultCompetitors.current = true;
      setSelectedCompetitorIds(new Set(availableCompetitors.map((c) => c.id)));
    }
  }, [availableCompetitors, preSelection.competitorIds.length]);

  // Validate current step. SELECT_LANDING_PAGE owns its own validity via
  // <LandingPageStep setStepValid>.
  useEffect(() => {
    switch (currentStep) {
      case CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS:
        setStepValid(selectedCompetitorIds.size > 0);
        break;
      case CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS:
        setStepValid(locations.length > 0);
        break;
    }
  }, [currentStep, selectedCompetitorIds, locations]);

  // Get step title based on current step
  const getStepTitle = () => {
    switch (currentStep) {
      case CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS:
        return 'Select Competitors';
      case CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE:
        return 'Select Landing Page';
      case CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS:
        return 'Where will your ads show?';
    }
  };

  // Handle competitor toggle
  const toggleCompetitor = (competitorId: string) => {
    setSelectedCompetitorIds((prev) => {
      const next = new Set(prev);
      if (next.has(competitorId)) {
        next.delete(competitorId);
      } else {
        next.add(competitorId);
      }
      return next;
    });
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS) {
      if (host) {
        host.onExit();
        return;
      }
      // First step - navigate back to origin page if set, otherwise default to /competitors
      navigate({ to: preSelection.origin || '/competitors' });
      return;
    }

    const backStep = STEP_CONFIG[currentStep].backStep;
    if (backStep) {
      setCurrentStep(backStep);
    }
  };


  // Handle next/submit
  const handleNext = async () => {
    switch (currentStep) {
      case CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS:
        setCurrentStep(CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE);
        break;

      case CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE:
        setCurrentStep(CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS);
        break;

      case CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS: {
        // Approve locations and create campaign
        const locationsApproved = campaignLocationsRef.current?.approveLocations();
        if (locationsApproved) {
          await handleCreateCampaign();
        }
        break;
      }
    }
  };

  // Create competitor campaign
  const handleCreateCampaign = async () => {
    if (!business || !organizationData) {
      toast({
        title: 'Error',
        description: 'Business or organization data not found.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedCompetitorIds.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one competitor.',
        variant: 'destructive',
      });
      return;
    }

    posthog?.capture('campaign_creation_submitted', {
      campaign_types: [CAMPAIGN_TYPE.COMPETITOR],
      competitor_count: selectedCompetitorIds.size,
      location_count: locations.length,
    });

    try {
      const competitorSlug = generateCampaignSlug();
      const result = await createCompetitorCampaignMutation.mutateAsync({
        campaign: {
          slug: competitorSlug,
          website_url: selectedLandingPage,
          competitor_ids: Array.from(selectedCompetitorIds),
          target_locations: locations,
          budget: 0,
          goal: 'competitor',
          organization_id: organizationData.id,
          business_id: business.id,
        },
        organizationId: organizationData.id,
      });

      // #5444: persist the wizard's Radius-tab selection on the new campaign.
      if (radii.length > 0 && result.campaign_id) {
        try {
          await setCampaignProximityTargets(result.campaign_id, radii);
        } catch {
          toast({
            description: 'Campaign created, but saving radius targets failed — set them in campaign settings.',
            variant: 'destructive',
          });
        }
      }

      await refetchWithDelayedUpdates();

      posthog?.capture('campaign_creation_completed', {
        created_campaigns: [CAMPAIGN_TYPE.COMPETITOR],
      });

      // Land on (and select) the new campaign so its generation progress is
      // visible, matching the brand/prospecting wizards. Seed the single-campaign
      // cache first so the detail loader resolves immediately instead of 404ing and
      // redirecting to /campaigns (#5444). The backend keeps the slug we generated,
      // and result.campaign_id is its id.
      // #5616: the competitor create response is not a full CampaignData, so seed a fully-typed
      // placeholder instead of an `as unknown as` cast. Arrays are empty (not undefined) so downstream
      // reads such as campaign.ads.length / .filter don't throw before the real data loads.
      // campaignQueryOptions' refetchInterval fills in the real campaign as generation completes.
      const placeholderCampaign: CampaignData = {
        id: result.campaign_id,
        slug: competitorSlug,
        name: '',
        state: CAMPAIGN_STATE.DRAFT,
        business_description: '',
        business_desc: '',
        goal: '',
        website_url: '',
        url: '',
        target_cpa_explanation: '',
        target_cpa: 0,
        budget: 0,
        target_locations: [],
        phone_numbers: [],
        ads: [],
        site_links: [],
        callouts: [],
        structured_snippets: [],
        prices: [],
        errors: [],
        settings_state: APPROVAL_STATE.DRAFT,
        assets_state: APPROVAL_STATE.DRAFT,
        budget_state: APPROVAL_STATE.DRAFT,
        user_account_id: 0,
        googleads_account_id: 0,
        googleadsaccount_id: 0,
        organization_id: 0,
        created_at: '',
        googleads_campaign: { campaign_id: result.campaign_id, googleads_campaign_id: '' },
        googleads_account: {
          id: '',
          currency: '',
          googleads_account_id: '',
          manager_account_id: null,
          name: '',
          selected: false,
          linked: '',
          user_account_id: 0,
        },
      };
      queryClient.setQueryData(campaignQueryOptions(competitorSlug).queryKey, placeholderCampaign);
      if (host) {
        host.onFinished({ campaignIds: result.campaign_id ? [result.campaign_id] : undefined });
      } else {
        navigate({
          to: '/campaigns/google/$campaignslug',
          params: { campaignslug: competitorSlug },
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create campaign';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      posthog?.capture('campaign_creation_failed', { error: errorMessage });
    }
  };

  // Determine button text
  const getNextButtonText = () => {
    if (currentStep === CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS) {
      return 'CREATE';
    }
    return 'CONTINUE';
  };

  return (
    <div className="relative p-8 w-full h-screen">
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
        <StepsIndicator
          currentStep={STEP_CONFIG[currentStep].stepIndicator + 1}
          totalSteps={3}
        />
        <div className="flex justify-center">
          <div className={currentStep === CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS ? 'w-full' : 'w-[60%]'}>
            <h1 className="text-3xl font-bold text-center mb-8">{getStepTitle()}</h1>
            <Card className="p-6">
              {currentStep === CAMPAIGN_CREATION_STEP.SELECT_LOCATIONS && (
                <CampaignLocations
                  ref={campaignLocationsRef}
                  onNext={() => {}}
                  setLocations={setLocations}
                  setStepValid={setStepValid}
                  locations={locations}
                  businessId={business?.id}
                  candidates={business?.target_locations ?? []}
                  setRadii={setRadii}
                />
              )}

              {/* Step 1: Select Competitors */}
              {currentStep === CAMPAIGN_CREATION_STEP.SELECT_COMPETITORS && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="space-y-4"
                >
                  <p className="text-gray-700">
                    Select competitors to target in your campaign. Only competitors not already in a campaign are shown.
                  </p>
                  {availableCompetitors.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {!competitors || competitors.length === 0
                        ? 'No competitors found. Generate competitors from the Competitors page first.'
                        : 'No available competitors. All competitors are already assigned to campaigns.'}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-4 pl-4">
                        <Checkbox
                          checked={
                            selectedCompetitorIds.size === availableCompetitors.length && availableCompetitors.length > 0
                              ? true
                              : selectedCompetitorIds.size > 0
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={() => {
                            if (selectedCompetitorIds.size > 0) {
                              setSelectedCompetitorIds(new Set());
                            } else {
                              setSelectedCompetitorIds(new Set(availableCompetitors.map((c) => c.id)));
                            }
                          }}
                        />
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="Search competitors..."
                            value={competitorSearchQuery}
                            onChange={(e) => setCompetitorSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div className="max-h-[30vh] overflow-y-auto space-y-2">
                        {filteredCompetitors.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No competitors found matching "{competitorSearchQuery}"
                          </div>
                        ) : (
                          filteredCompetitors.map((competitor) => (
                            <label
                              key={competitor.id}
                              className="flex items-center space-x-4 p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={selectedCompetitorIds.has(competitor.id)}
                                onCheckedChange={() => toggleCompetitor(competitor.id)}
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {competitor.competitor_brand_name}
                                </div>
                                {competitor.primary_website && (
                                  <div className="text-sm text-gray-500">
                                    {competitor.primary_website}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                      <div className="text-sm text-gray-500 text-center">
                        {selectedCompetitorIds.size} of {availableCompetitors.length} competitors selected
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {/* Step 2: Select Landing Page */}
              {currentStep === CAMPAIGN_CREATION_STEP.SELECT_LANDING_PAGE && (
                <LandingPageStep
                  selectedUrl={selectedLandingPage}
                  onSelect={setSelectedLandingPage}
                  setStepValid={setStepValid}
                  instruction="Choose the landing page for your competitor campaign."
                />
              )}

            </Card>

            <div className="mx-auto flex w-full max-w-[614px] justify-between items-center pt-6">
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>BACK</span>
              </Button>

              <Button
                size="lg"
                disabled={!stepValid || isLoading}
                onClick={handleNext}
                className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
              >
                <span>{getNextButtonText()}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
